import { useState, useEffect } from 'react';
import { productsAPI } from '../services/api';
import {
  ProductOption,
  ProductOptionValue,
  SelectedOptionValue,
} from '../types/productOptions';

interface ProductOptionsSelectorProps {
  productId: number;
  basePrice: number; // In cents
  onPriceChange?: (totalPrice: number) => void;
  onSelectionChange?: (selections: SelectedOptionValue[]) => void;
  onValidationChange?: (isValid: boolean) => void;
  onOptionsLoaded?: (hasOptions: boolean) => void;
}

export const ProductOptionsSelector = ({
  productId,
  basePrice,
  onPriceChange,
  onSelectionChange,
  onValidationChange,
  onOptionsLoaded,
}: ProductOptionsSelectorProps) => {
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [selections, setSelections] = useState<Map<number, number | number[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoading(true);
        const raw = await productsAPI.getOptions(productId);

        // priceModifier crosses the wire as a decimal string ("0.00"), because
        // it is a Postgres numeric. Every price in this component is hidden
        // behind `priceModifier !== 0`, and "0.00" !== 0 is true, so a free
        // choice rendered a pointless "RM 0.00" next to its label. Coerce once
        // here rather than at each comparison.
        const data = (raw || []).map((option: any) => ({
          ...option,
          values: (option.values || []).map((value: any) => ({
            ...value,
            priceModifier: Number(value.priceModifier) || 0,
          })),
        }));
        setOptions(data);

        // Pre-select default values
        const defaultSelections = new Map<number, number | number[]>();
        data.forEach((option: ProductOption) => {
          if (option.type === 'single_select') {
            const defaultValue = option.values.find((v) => v.isDefault);
            if (defaultValue) {
              defaultSelections.set(option.id, defaultValue.id);
            }
          } else if (option.type === 'multi_select') {
            const defaultValues = option.values
              .filter((v) => v.isDefault)
              .map((v) => v.id);
            if (defaultValues.length > 0) {
              defaultSelections.set(option.id, defaultValues);
            }
          }
        });
        setSelections(defaultSelections);
      } catch (err) {
        console.error('Failed to fetch product options:', err);
        setError('Failed to load product options');
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, [productId]);

  // Notify parent when options are loaded
  useEffect(() => {
    if (!loading && onOptionsLoaded) {
      onOptionsLoaded(options.length > 0);
    }
  }, [loading, options.length, onOptionsLoaded]);

  const calculatePrice = () => {
    let totalModifier = 0;
    const selectedOptions: SelectedOptionValue[] = [];
    let isValid = true;

    options.forEach((option) => {
      const selection = selections.get(option.id);

      // Check if required option is selected
      if (option.required && !selection) {
        isValid = false;
      }

      if (selection) {
        if (option.type === 'single_select' && typeof selection === 'number') {
          const value = option.values.find((v) => v.id === selection);
          if (value) {
            const modifier = Number(value.priceModifier) || 0;
            totalModifier += modifier;
            selectedOptions.push({
              optionId: option.id,
              optionName: option.name,
              valueId: value.id,
              valueLabel: value.label,
              priceModifier: modifier,
            });
          }
        } else if (option.type === 'multi_select' && Array.isArray(selection)) {
          selection.forEach((valueId) => {
            const value = option.values.find((v) => v.id === valueId);
            if (value) {
              const modifier = Number(value.priceModifier) || 0;
              totalModifier += modifier;
              selectedOptions.push({
                optionId: option.id,
                optionName: option.name,
                valueId: value.id,
                valueLabel: value.label,
                priceModifier: modifier,
              });
            }
          });
        }
      }
    });

    // Ensure basePrice is a valid number
    const validBasePrice = Number(basePrice) || 0;
    const validTotalModifier = Number(totalModifier) || 0;

    return {
      totalPrice: validBasePrice + validTotalModifier,
      selectedOptions,
      isValid,
    };
  };

  // Calculate total price and validate whenever selections change
  useEffect(() => {
    const { totalPrice, selectedOptions, isValid } = calculatePrice();

    if (onPriceChange) {
      onPriceChange(totalPrice);
    }

    if (onSelectionChange) {
      onSelectionChange(selectedOptions);
    }

    if (onValidationChange) {
      onValidationChange(isValid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections, options]);

  const handleSingleSelect = (optionId: number, valueId: number) => {
    setSelections((prev) => {
      const newSelections = new Map(prev);
      newSelections.set(optionId, valueId);
      return newSelections;
    });
  };

  const handleMultiSelect = (optionId: number, valueId: number, checked: boolean) => {
    setSelections((prev) => {
      const newSelections = new Map(prev);
      const current = newSelections.get(optionId) as number[] || [];

      if (checked) {
        newSelections.set(optionId, [...current, valueId]);
      } else {
        newSelections.set(
          optionId,
          current.filter((id) => id !== valueId)
        );
      }

      return newSelections;
    });
  };

  if (loading) {
    return (
      <div className="py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-32"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-6 text-[color:var(--color-error)] text-sm">
        {error}
      </div>
    );
  }

  if (options.length === 0) {
    return null; // No options to display
  }

  return (
    <div className="space-y-6 py-6">
      <h3 className="text-lg font-semibold text-[color:var(--color-text)]">
        Customize Your Order
      </h3>

      {options.map((option) => (
        <div key={option.id} className="space-y-3">
          <label className="block text-sm font-medium text-[color:var(--color-text)]">
            {option.name}
            {option.required && <span className="text-[color:var(--color-error)] ml-1">*</span>}
          </label>

          {option.type === 'single_select' ? (
            <SingleSelectOption
              option={option}
              selectedValue={selections.get(option.id) as number}
              onSelect={(valueId) => handleSingleSelect(option.id, valueId)}
            />
          ) : (
            <MultiSelectOption
              option={option}
              selectedValues={(selections.get(option.id) as number[]) || []}
              onToggle={(valueId, checked) => handleMultiSelect(option.id, valueId, checked)}
            />
          )}
        </div>
      ))}
    </div>
  );
};

// Single Select Option Component - Dropdown Style
interface SingleSelectOptionProps {
  option: ProductOption;
  selectedValue?: number;
  onSelect: (valueId: number) => void;
}

/** Lightweight CSS-color heuristic: try the label as a CSS color. */
const cssColor = (label: string): string | null => {
  if (typeof document === 'undefined') return null;
  const probe = document.createElement('span');
  probe.style.color = '';
  probe.style.color = label.trim().toLowerCase();
  return probe.style.color ? probe.style.color : null;
};

const isColorOption = (name: string): boolean =>
  /^(color|colou?r|shade|finish)$/i.test(name.trim());

const SingleSelectOption = ({ option, selectedValue, onSelect }: SingleSelectOptionProps) => {
  const selectedValueObj = option.values.find((v) => v.id === selectedValue);
  const sorted = [...option.values].sort((a, b) => a.sortOrder - b.sortOrder);
  const colorMode = isColorOption(option.name);

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {sorted.map((value) => {
          const isSelected = value.id === selectedValue;
          const isOOS = !value.available;
          const swatchColor = colorMode ? cssColor(value.label) : null;

          // Swatch-only (color) variant
          if (swatchColor) {
            return (
              <button
                key={value.id}
                type="button"
                disabled={isOOS}
                onClick={() => !isOOS && onSelect(value.id)}
                aria-label={value.label + (isOOS ? ' (out of stock)' : '')}
                aria-pressed={isSelected}
                title={value.label + (isOOS ? ' — out of stock' : '')}
                className={`relative w-9 h-9 rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--primary-color,#0F766E)] ${
                  isSelected
                    ? 'ring-2 ring-offset-2 ring-[var(--primary-color,#0F766E)]'
                    : 'ring-1 ring-black/10 hover:ring-black/30'
                } ${isOOS ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                style={{ backgroundColor: swatchColor }}
              >
                {isOOS && (
                  <span
                    aria-hidden
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <svg viewBox="0 0 36 36" className="w-full h-full">
                      <line
                        x1="6"
                        y1="30"
                        x2="30"
                        y2="6"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="text-white mix-blend-difference"
                      />
                    </svg>
                  </span>
                )}
              </button>
            );
          }

          // Default labeled-pill swatch
          return (
            <button
              key={value.id}
              type="button"
              disabled={isOOS}
              onClick={() => !isOOS && onSelect(value.id)}
              aria-pressed={isSelected}
              aria-label={value.label + (isOOS ? ' (out of stock)' : '')}
              className={`relative px-4 py-2 rounded-full text-sm font-medium border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--primary-color,#0F766E)] ${
                isSelected
                  ? 'border-[var(--primary-color,#0F766E)] bg-[color-mix(in_srgb,var(--primary-color,#0F766E)_8%,transparent)] text-[color:var(--card-text,#111827)]'
                  : isOOS
                    ? 'border-neutral-200 text-neutral-400 line-through cursor-not-allowed'
                    : 'border-neutral-300 text-[color:var(--card-text,#111827)] hover:border-neutral-500 cursor-pointer'
              }`}
            >
              {value.label}
              {option.displayPrice && value.priceModifier !== 0 && !isOOS && (
                <span className="ml-1.5 text-xs opacity-70 font-normal">
                  {value.priceModifier > 0 ? '+' : ''}RM
                  {(value.priceModifier / 100).toFixed(2)}
                </span>
              )}
              {isOOS && (
                <span className="ml-1.5 text-[10px] uppercase tracking-wider font-semibold text-neutral-400">
                  Out
                </span>
              )}
            </button>
          );
        })}
      </div>

      {selectedValueObj?.label && (
        <p className="mt-2 text-xs text-[color:var(--card-muted,#6b7280)]">
          Selected: <span className="font-medium text-[color:var(--card-text,#111827)]">{selectedValueObj.label}</span>
          {option.displayPrice && selectedValueObj.priceModifier !== 0 && (
            <span className="ml-2">
              {selectedValueObj.priceModifier > 0 ? '+' : ''}RM
              {(selectedValueObj.priceModifier / 100).toFixed(2)}
            </span>
          )}
        </p>
      )}
    </div>
  );
};

// Multi Select Option Component
interface MultiSelectOptionProps {
  option: ProductOption;
  selectedValues: number[];
  onToggle: (valueId: number, checked: boolean) => void;
}

const MultiSelectOption = ({ option, selectedValues, onToggle }: MultiSelectOptionProps) => {
  return (
    <div className="space-y-2">
      {option.values
        .filter((v) => v.available)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((value) => {
          const isChecked = selectedValues.includes(value.id);
          return (
            <label
              key={value.id}
              className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${isChecked
                ? 'border-orange-500 bg-orange-50'
                : 'border-gray-300 hover:border-gray-400'
                }`}
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => onToggle(value.id, e.target.checked)}
                  className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                />
                <span className={`text-sm font-medium ${isChecked ? 'text-orange-900' : 'text-[color:var(--color-text)]'}`}>
                  {value.label}
                </span>
              </div>

              {option.displayPrice && value.priceModifier !== 0 && (
                <span className={`text-sm font-semibold ${value.priceModifier > 0 ? 'text-[color:var(--color-success)]' : 'text-[color:var(--color-error)]'
                  }`}>
                  {value.priceModifier > 0 ? '+' : ''}RM {(value.priceModifier / 100).toFixed(2)}
                </span>
              )}
            </label>
          );
        })}
    </div>
  );
};